import { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Avatar,
  Button,
  Fade,
  Pagination,
} from "@mui/material";

import {
  LocationCityOutlined,
  Phone,
  Email,
  KeyboardArrowDown,
  Check,
} from "@mui/icons-material";
import { colors } from "./Followup.styles";
import {
  MarkDoneSidebar,
  TelecallerMenu,
  PriorityMenu,
  ContactInfoPopup,
} from "./components";
import EmptyLeadsState from "../../Leads/EmptyLeads";

const FollowupTable = ({
  tasks,
  page,
  onPageChange,
  totalTasks,
  rowsPerPage,
  onReschedule,
  onMarkDone,
  onViewDetails,
  onTelecallerChange,
  onPriorityChange,
  telecallers,
  searchQuery,
}) => {
  const [hoveredRow, setHoveredRow] = useState(null);
  const [telecallerAnchorEl, setTelecallerAnchorEl] = useState(null);
  const [selectedTaskForTelecaller, setSelectedTaskForTelecaller] =
    useState(null);
  const [markedDoneTasks, setMarkedDoneTasks] = useState({});
  const [markDoneSidebarOpen, setMarkDoneSidebarOpen] = useState(false);
  const [selectedTaskForMarkDone, setSelectedTaskForMarkDone] = useState(null);
  const [markDoneNotes, setMarkDoneNotes] = useState("");

  // Priority menu state
  const [priorityAnchorEl, setPriorityAnchorEl] = useState(null);
  const [selectedTaskForPriority, setSelectedTaskForPriority] = useState(null);

  // Contact info popup state
  const [contactPopupOpen, setContactPopupOpen] = useState(false);
  const [selectedContactInfo, setSelectedContactInfo] = useState(null);

  const users = telecallers || [
    "Rahul S.",
    "Priya K.",
    "Amit D.",
    "Neha R.",
    "Unassigned",
  ];

  const sampleTasks = [
    {
      id: 1,
      time: "10:30 AM",
      timeLabel: "Overdue",
      timeLabelColor: "#EF4444",
      priority: "HIGH",
      priorityBg: "#FEE2E2",
      priorityColor: "#DC2626",
      client: "Jane Doe",
      initials: "JD",
      avatarBg: "#E0E7FF",
      avatarColor: "#6366F1",
      lastInteraction: "Spoke 2 days ago",
      telecaller: "Rahul S.",

    },
    {
      id: 2,
      time: "11:15 AM",
      timeLabel: "In 15 mins",
      timeLabelColor: "#6B7280",
      priority: "MEDIUM",
      priorityBg: "#FEF3C7",
      priorityColor: "#D97706",
      client: "Michael Chen",
      initials: "MC",
      avatarBg: "#DBEAFE",
      avatarColor: "#3B82F6",
      lastInteraction: "Left Voicemail yesterday",
      telecaller: "Priya K.",
    },
    {
      id: 3,
      time: "01:45 PM",
      timeLabel: "In 2 hours",
      timeLabelColor: "#6B7280",
      priority: "MEDIUM",
      priorityBg: "#FEF3C7",
      priorityColor: "#D97706",
      client: "Sarah Williams",
      initials: "SW",
      avatarBg: "#D1FAE5",
      avatarColor: "#10B981",
      lastInteraction: "First contact today",
      telecaller: "Unassigned",
    },
  ];

  // Format date for display
  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const displayTasks =
    tasks?.length > 0
      ? tasks.map((task) => ({
          id: task.id,
          leadId: task.leadId,
          dueDate: formatDate(task.dueDate),
          dueTime: task.dueTime || task.time,
          isOverdue: task.isOverdue,
          timeLabel:
            task.timeLabel || (task.isOverdue ? "Overdue" : task.timeAgo || ""),
          timeLabelColor:
            task.timeLabelColor || (task.isOverdue ? "#EF4444" : "#6B7280"),
          priority: task.priority || "Medium",
          priorityDisplay: (task.priority || "Medium").toUpperCase(),
          priorityBg:
            (task.priority || "Medium").toLowerCase() === "high"
              ? "#FEE2E2"
              : (task.priority || "Medium").toLowerCase() === "medium"
                ? "#FEF3C7"
                : "#E2E8F0",
          priorityColor:
            (task.priority || "Medium").toLowerCase() === "high"
              ? "#DC2626"
              : (task.priority || "Medium").toLowerCase() === "medium"
                ? "#D97706"
                : "#64748B",
          client: task.clientName || task.client,
          initials: (task.clientName || task.client || "")
            .split(" ")
            .map((n) => n[0])
            .join(""),
          avatarBg: "#E0E7FF",
          avatarColor: "#6366F1",
          lastInteraction: task.lastInteraction,
          telecaller: task.telecaller || "Unassigned",
          contact: task.contact,
          email: task.email,
          address: task.address,
          followup_status: task.followup_status || "pending",
        }))
      : tasks?.length === 0
        ? []
        : sampleTasks;

  const getInitials = (name) => {
    if (!name) return "??";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const handleTelecallerClick = (event, task) => {
    event.stopPropagation();
    setTelecallerAnchorEl(event.currentTarget);
    setSelectedTaskForTelecaller(task);
  };

  const handleTelecallerClose = () => {
    setTelecallerAnchorEl(null);
    setSelectedTaskForTelecaller(null);
  };

  const handleSelectUser = (user) => {
    if (onTelecallerChange && selectedTaskForTelecaller) {
      onTelecallerChange(selectedTaskForTelecaller, user);
    }
    handleTelecallerClose();
  };

  // Priority handlers
  const handlePriorityClick = (event, task) => {
    event.stopPropagation();
    setPriorityAnchorEl(event.currentTarget);
    setSelectedTaskForPriority(task);
  };

  const handlePriorityClose = () => {
    setPriorityAnchorEl(null);
    setSelectedTaskForPriority(null);
  };

  const handlePrioritySelect = (priority) => {
    if (onPriorityChange && selectedTaskForPriority) {
      onPriorityChange(selectedTaskForPriority, priority);
    }
    handlePriorityClose();
  };

  // Contact info handlers
  const handleContactInfoClick = (event, task) => {
    event.stopPropagation();
    setSelectedContactInfo({
      phone: task.contact,
      email: task.email,
      address: task.address || "",
      clientName: task.client,
    });
    setContactPopupOpen(true);
  };

  const handleContactPopupClose = () => {
    setContactPopupOpen(false);
    setSelectedContactInfo(null);
  };

  const handleRescheduleClick = (event, task) => {
    event.stopPropagation();
    if (onReschedule) {
      onReschedule(task);
    }
  };

  const handleMarkDoneClick = (event, task) => {
    event.stopPropagation();
    setMarkedDoneTasks((prev) => ({
      ...prev,
      [task.id]: !prev[task.id],
    }));
    setSelectedTaskForMarkDone(task);
    setMarkDoneSidebarOpen(true);
    setMarkDoneNotes("");
  };

  const handleMarkDoneSidebarClose = () => {
    setMarkDoneSidebarOpen(false);
    setSelectedTaskForMarkDone(null);
    setMarkDoneNotes("");
  };

  const handleConfirmMarkDone = () => {
    if (onMarkDone && selectedTaskForMarkDone) {
      onMarkDone({ ...selectedTaskForMarkDone, notes: markDoneNotes });
    }
    handleMarkDoneSidebarClose();
  };

  const handleCancelMarkDone = () => {
    if (selectedTaskForMarkDone) {
      setMarkedDoneTasks((prev) => ({
        ...prev,
        [selectedTaskForMarkDone.id]: false,
      }));
    }
    handleMarkDoneSidebarClose();
  };

  const handleViewDetailsClick = (event, task) => {
    event.stopPropagation();
    if (onViewDetails) {
      onViewDetails(task);
    }
  };

  const totalPages = Math.ceil(
    (totalTasks || displayTasks.length) / (rowsPerPage || 10),
  );
  const startIndex = ((page || 1) - 1) * (rowsPerPage || 10) + 1;
  const endIndex = Math.min(
    (page || 1) * (rowsPerPage || 10),
    totalTasks || displayTasks.length,
  );

  return (
    <Box sx={{ bgcolor: "#FFFFFF", minHeight: "70vh", p: 0 }}>
      {/* Table Container */}
      <Box
        sx={{
          bgcolor: "white",
          borderRadius: "12px",
          overflow: "auto",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          border: "1px solid #E2E8F0",
        }}
      >
        {/* Table Header */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns:
              "minmax(90px, 1fr) minmax(80px, 0.9fr) minmax(160px, 1.6fr) minmax(110px, 1.1fr) minmax(150px, 1.5fr) minmax(120px, 1.2fr) minmax(200px, 2fr) minmax(110px, 1.1fr)",
            alignItems: "center",
            px: 2.5,
            py: 1.5,
            bgcolor: "#FAFAFA",
            borderBottom: "1px solid #E2E8F0",
            gap: 1,
          }}
        >
          {[
            "DUE DATE",
            "PRIORITY",
            "CLIENT",
            "CONTACT INFO",
            "LAST INTERACTION",
            "TELECALLER",
            "STATUS",
            "ACTIONS",
          ].map((header) => (
            <Typography
              key={header}
              sx={{
                fontSize: 10,
                fontWeight: 600,
                color: "#9CA3AF",
                letterSpacing: 0.8,
                textTransform: "uppercase",
              }}
            >
              {header}
            </Typography>
          ))}
        </Box>

        {/* Show No Data or Table Rows */}
        {displayTasks.length === 0 ? (
          <EmptyLeadsState />
        ) : (
          <>
            {/* Table Rows */}
            {displayTasks.map((task, index) => (
              <Fade in timeout={300 + index * 50} key={task.id || index}>
                <Box
                  onMouseEnter={() => setHoveredRow(index)}
                  onMouseLeave={() => setHoveredRow(null)}
                  onClick={() => onViewDetails && onViewDetails(task)}
                  sx={{
                    display: "grid",
                    gridTemplateColumns:
                      "minmax(90px, 1fr) minmax(80px, 0.9fr) minmax(160px, 1.6fr) minmax(110px, 1.1fr) minmax(150px, 1.5fr) minmax(120px, 1.2fr) minmax(200px, 2fr) minmax(110px, 1.1fr)",
                    alignItems: "center",
                    px: 2.5,
                    py: 2,
                    bgcolor: hoveredRow === index ? "#FAFBFC" : "white",
                    borderBottom: "1px solid #F5F5F5",
                    transition: "all 0.2s ease",
                    cursor: "pointer",
                    gap: 1,
                    minWidth: "1035px",
                    borderRadius: 4,
                  }}
                >
                  {/* Due Date */}
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color:
                          task.timeLabel === "Overdue" ? "#EF4444" : "#111827",
                        mb: 0.25,
                        lineHeight: 1.4,
                      }}
                    >
                      {task.dueDate}
                    </Typography>
                    {task.timeLabel && (
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: task.timeLabelColor,
                          fontWeight: 400,
                          lineHeight: 1.4,
                        }}
                      >
                        {task.timeLabel}
                      </Typography>
                    )}
                  </Box>

                  {/* Priority - Clickable dropdown */}
                  <Box
                    onClick={(e) => handlePriorityClick(e, task)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      "&:hover": {
                        opacity: 0.8,
                      },
                    }}
                  >
                    <Chip
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          {task.priorityDisplay}
                          <KeyboardArrowDown sx={{ fontSize: 14, ml: -0.25 }} />
                        </Box>
                      }
                      size="small"
                      sx={{
                        bgcolor: task.priorityBg,
                        color: task.priorityColor,
                        fontWeight: 700,
                        fontSize: 9,
                        borderRadius: 10,
                        height: 22,
                        cursor: "pointer",
                        "& .MuiChip-label": {
                          px: 0.75,
                          py: 0,
                          display: "flex",
                          alignItems: "center",
                        },
                      }}
                    />
                  </Box>

                  {/* Client */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Avatar
                      sx={{
                        width: 28,
                        height: 28,
                        bgcolor: task.avatarBg,
                        color: task.avatarColor,
                        fontSize: 11,
                        fontWeight: 600,
                      }}
                    >
                      {task.initials || getInitials(task.client)}
                    </Avatar>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: "#111827",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {task.client}
                    </Typography>
                  </Box>

                  {/* Contact Info - Click to open popup */}
                  <Box
                    onClick={(e) => handleContactInfoClick(e, task)}
                    sx={{
                      display: "flex",
                      gap: 0.5,
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                  >
                    <IconButton
                      size="small"
                      sx={{
                        width: 26,
                        height: 26,
                        bgcolor: "#F1F5F9",
                        borderRadius: "6px",
                        "&:hover": {
                          bgcolor: "#E2E8F0",
                        },
                      }}
                    >
                      <LocationCityOutlined
                        sx={{ fontSize: 14, color: "#64748B" }}
                      />
                    </IconButton>
                    <IconButton
                      size="small"
                      sx={{
                        width: 26,
                        height: 26,
                        bgcolor: "#F1F5F9",
                        borderRadius: "6px",
                        "&:hover": {
                          bgcolor: "#E2E8F0",
                        },
                      }}
                    >
                      <Phone sx={{ fontSize: 14, color: "#64748B" }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      sx={{
                        width: 26,
                        height: 26,
                        bgcolor: "#F1F5F9",
                        borderRadius: "6px",
                        "&:hover": {
                          bgcolor: "#E2E8F0",
                        },
                      }}
                    >
                      <Email sx={{ fontSize: 14, color: "#64748B" }} />
                    </IconButton>
                  </Box>

                  {/* Last Interaction */}
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "#6B7280",
                        fontWeight: 400,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {task.lastInteraction}
                    </Typography>
                  </Box>

                  {/* Telecaller */}
                  <Box
                    onClick={(e) => handleTelecallerClick(e, task)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      maxWidth: "100%",
                      "&:hover": {
                        "& .telecaller-text": {
                          color: colors.primary,
                        },
                      },
                    }}
                  >
                    <Typography
                      className="telecaller-text"
                      sx={{
                        fontSize: 12,
                        color:
                          task.telecaller === "Unassigned"
                            ? "#9CA3AF"
                            : "#374151",
                        fontWeight: 400,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: 90,
                      }}
                    >
                      {task.telecaller}
                    </Typography>
                    <KeyboardArrowDown
                      sx={{
                        fontSize: 14,
                        color: "#9CA3AF",
                        flexShrink: 0,
                      }}
                    />
                  </Box>

                  {/* Status */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      minWidth: 0,
                    }}
                  >
                    {task.followup_status === "completed" && (
                      <>
                        <Check
                          sx={{
                            fontSize: 16,
                            color: "#10B981",
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: 10,
                            fontWeight: 600,
                            color: "#10B981",
                            letterSpacing: 0.3,
                            textTransform: "uppercase",
                            whiteSpace: "nowrap",
                          }}
                        >
                          COMPLETED
                        </Typography>
                      </>
                    )}
                    <Typography
                      onClick={(e) => handleRescheduleClick(e, task)}
                      sx={{
                        fontSize: 10,
                        fontWeight: 500,
                        color: "#6B7280",
                        letterSpacing: 0.3,
                        textTransform: "uppercase",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        whiteSpace: "nowrap",
                        "&:hover": {
                          color: colors.primary,
                        },
                      }}
                    >
                      {task.followup_status === "completed"
                        ? "RESCHEDULE AGAIN"
                        : "RESCHEDULE"}
                    </Typography>

                    {task.followup_status !== "completed" && (
                      <>
                        <Box
                          onClick={(e) => handleMarkDoneClick(e, task)}
                          sx={{
                            width: 18,
                            height: 18,
                            borderRadius: "50%",
                            border: markedDoneTasks[task.id]
                              ? "none"
                              : "2px solid #E5E7EB",
                            bgcolor: markedDoneTasks[task.id]
                              ? "#10B981"
                              : "transparent",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            transition: "all 0.2s",
                            flexShrink: 0,
                            "&:hover": {
                              borderColor: "#10B981",
                              bgcolor: markedDoneTasks[task.id]
                                ? "#10B981"
                                : "#D1FAE5",
                            },
                          }}
                        >
                          {markedDoneTasks[task.id] && (
                            <Check sx={{ fontSize: 12, color: "#fff" }} />
                          )}
                        </Box>
                        <Typography
                          onClick={(e) => handleMarkDoneClick(e, task)}
                          sx={{
                            fontSize: 10,
                            fontWeight: 500,
                            color: markedDoneTasks[task.id]
                              ? "#10B981"
                              : "#9CA3AF",
                            letterSpacing: 0.3,
                            textTransform: "uppercase",
                            cursor: "pointer",
                            transition: "all 0.2s",
                            whiteSpace: "nowrap",
                            "&:hover": {
                              color: "#10B981",
                            },
                          }}
                        >
                          MARK DONE
                        </Typography>
                      </>
                    )}
                  </Box>

                  {/* Actions */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(e) => handleViewDetailsClick(e, task)}
                      sx={{
                        textTransform: "none",
                        fontSize: 11,
                        fontWeight: 500,
                        color: "primary.main",
                        bgcolor: "transparent",
                        borderColor: "primary.main",
                        borderRadius: "6px",
                        px: 1.3,
                        py: 0.5,
                        minWidth: "auto",
                        transition: "all 0.2s",
                        "&:hover": {
                          bgcolor: "primary.main",
                          color: "white",
                          borderColor: "primary.main",
                        },
                        "&:active": {
                          bgcolor: "primary.dark",
                          borderColor: "primary.dark",
                        },
                      }}
                    >
                      VIEW DETAILS
                    </Button>
                  </Box>
                </Box>
              </Fade>
            ))}

            {/* Table Footer Pagination */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2.5,
                py: 1.5,
                borderTop: "1px solid #E2E8F0",
                bgcolor: "#FAFAFA",
                minWidth: "1035px",
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  color: "#9CA3AF",
                  fontWeight: 500,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}
              >
                SHOWING {startIndex}-{endIndex} OF{" "}
                {totalTasks || displayTasks.length} TASKS
              </Typography>

              <Pagination
                count={totalPages || 1}
                page={page || 1}
                onChange={(e, val) => onPageChange && onPageChange(val)}
                size="small"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  "& .MuiPaginationItem-root": {
                    height: 34,
                    minWidth: 34,
                    borderRadius: 3,
                    fontSize: 16,
                    fontWeight: 700,
                    border: "2px solid transparent",
                    color: "#64748B",
                    boxSizing: "border-box",
                    transition: "all 0.2s ease",
                  },
                  "& .MuiPaginationItem-root.Mui-selected": {
                    bgcolor: "#fff",
                    borderColor: "#E2E8F0",
                    color: "primary.main",
                    "&:hover": {
                      bgcolor: "#F8FAFC",
                    },
                  },
                  "& .MuiPaginationItem-previousNext": {
                    bgcolor: "#fff",
                    borderColor: "#E2E8F0",
                    color: "#94A3B8",
                    "& svg": {
                      fontSize: 24,
                      fontWeight: 700,
                    },
                    "&:hover": {
                      bgcolor: "#F8FAFC",
                    },
                  },
                  "& .MuiPaginationItem-page": {
                    bgcolor: "#fff",
                    borderColor: "#E2E8F0",
                    "&:hover": {
                      bgcolor: "#F8FAFC",
                    },
                  },
                  "& .MuiPaginationItem-previousNext.Mui-disabled": {
                    borderColor: "#E5E7EB",
                    color: "#CBD5E1",
                  },
                }}
              />
            </Box>
          </>
        )}
      </Box>

      {/* Telecaller Menu */}
      <TelecallerMenu
        anchorEl={telecallerAnchorEl}
        open={Boolean(telecallerAnchorEl)}
        onClose={handleTelecallerClose}
        users={users}
        onSelect={handleSelectUser}
      />

      {/* Mark Done Sidebar */}
      <MarkDoneSidebar
        open={markDoneSidebarOpen}
        onClose={handleCancelMarkDone}
        task={selectedTaskForMarkDone}
        notes={markDoneNotes}
        onNotesChange={setMarkDoneNotes}
        onConfirm={handleConfirmMarkDone}
      />

      {/* Priority Menu */}
      <PriorityMenu
        anchorEl={priorityAnchorEl}
        open={Boolean(priorityAnchorEl)}
        onClose={handlePriorityClose}
        currentPriority={selectedTaskForPriority?.priority}
        onSelect={handlePrioritySelect}
      />

      {/* Contact Info Popup */}
      <ContactInfoPopup
        open={contactPopupOpen}
        onClose={handleContactPopupClose}
        contact={selectedContactInfo}
      />
    </Box>
  );
};

export default FollowupTable;
